import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import MuiAlert from "@material-ui/lab/Alert";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";

// @material-ui/icons
import { Email } from "components/MaterialSymbol/MaterialIcons";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardFooter from "components/Card/CardFooter.js";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

//libs
import { useFormik } from "formik";
import * as yup from "yup";

//const
export const INITIAL_VALUES = {
  email: "",
  password: "",
};

const defValidationSchema = {
  email: yup.string().required(),
  password: yup.string().required(),
};

import UserService from "services/UserService";
import DealerService from "services/DealerService";
import { isAdminUiSkinEnabled } from "adminUiSkin";
import { isMfaEnabled } from "adminMfa";

const useStyles = makeStyles(styles);

export default function LoginPage() {
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  let dlid = query.get("dlid");
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "error",
  });
  const [loading, setLoading] = useState(false);
  const [cardAnimaton, setCardAnimation] = useState("cardHidden");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPendingToken, setMfaPendingToken] = useState("");
  const [resolvedDlid, setResolvedDlid] = useState(dlid || "");
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUES,
    validationSchema: yup.object(defValidationSchema),
    onSubmit: async (values) => {
      setLoading(true);
      const finalDlid = await resolveDlid();
      const loginPayload = {
        ...values,
        dlid: finalDlid,
      };

      setResolvedDlid(finalDlid || "");
      setCurrentUserEmail(values.email);

      if (isMfaEnabled()) {
        await requestMfaLogin(loginPayload);
        return;
      }

      await login(loginPayload);
    },
  });

  const resolveDlid = async () => {
    if (dlid) return dlid;

    const dealers = await DealerService.getAll("").then((d) => d.data);
    const cb = (dealer) => dealer.dealerAdminDomain === window.location.hostname;

    if (dealers.some(cb)) {
      dlid = dealers.find(cb).id;
    }

    return dlid;
  };

  const finishLogin = (user, currentDlid) => {
    localStorage.setItem("_gid", user);
    localStorage.setItem("_sdlid", currentDlid ? 1 : 0);
    if (currentDlid) localStorage.setItem("_dlid", currentDlid);

    const redirectAfterLogin =
      localStorage.getItem("redirectAfterLogin") || "/admin";

    const url = new URL(redirectAfterLogin, window.location.origin);
    const dlidFromRedirect = url.searchParams.get("dlid");
    if (dlidFromRedirect) {
      localStorage.setItem("_dlid", dlidFromRedirect);
    }

    const redirectPathname = url.pathname;
    localStorage.removeItem("redirectAfterLogin");
    window.location.pathname = redirectPathname;
  };

  const login = async (body) => {
    try {
      const user = await UserService.login(body);
      finishLogin(user, body.dlid);
    } catch (error) {
      setLoading(false);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || "Login failed. Please try again.",
        severity: "error",
      });
    }
  };

  const requestMfaLogin = async (body) => {
    try {
      const response = await UserService.requestMfaLogin(body);
      setMfaPendingToken(response.pendingToken);
      setMfaCode("");
      setAlert({
        show: true,
        message:
          response.message ||
          "A verification code was sent to your email address.",
        severity: "info",
      });
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          "Unable to start MFA verification. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyMfaLogin = async () => {
    if (!mfaCode.trim()) {
      setAlert({
        show: true,
        message: "Verification code is required.",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await UserService.verifyMfaLogin({
        pendingToken: mfaPendingToken,
        code: mfaCode.trim(),
      });
      finishLogin(response.user, resolvedDlid);
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          "Unable to verify the MFA code. Please try again.",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const resendMfaLogin = async () => {
    setLoading(true);

    try {
      const response = await UserService.resendMfaLogin({
        pendingToken: mfaPendingToken,
      });
      setAlert({
        show: true,
        message:
          response.message || "A new verification code was sent to your email address.",
        severity: "info",
      });
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          "Unable to resend the MFA code. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetMfaStep = () => {
    setMfaPendingToken("");
    setMfaCode("");
    setAlert({
      show: false,
      message: "",
      severity: "error",
    });
  };

  useEffect(() => {
    let id = setTimeout(function () {
      setCardAnimation("");
    }, 700);
    // Cleanup
    return function cleanup() {
      window.clearTimeout(id);
    };
  }, []);

  const classes = useStyles();
  const isShadcn = isAdminUiSkinEnabled();
  const isMfaStep = Boolean(mfaPendingToken);

  const handleFormSubmit = (event) => {
    event.preventDefault();

    if (isMfaStep) {
      verifyMfaLogin();
      return;
    }

    formik.handleSubmit();
  };

  useEffect(() => {
    if (!isShadcn) {
      document.body.classList.remove("shadcn-login-active");
      return;
    }

    document.body.classList.add("shadcn-login-active");

    return () => {
      document.body.classList.remove("shadcn-login-active");
    };
  }, [isShadcn]);

  if (isShadcn) {
    return (
      <div className="shadcn-login-page">
        <form className="shadcn-login-card" onSubmit={handleFormSubmit}>
          <h1>Sign in</h1>
          {alert.show ? (
            <MuiAlert
              className="shadcn-login-alert"
              onClose={() => setAlert({ ...alert, show: false })}
              severity={alert.severity}
            >
              {alert.message}
            </MuiAlert>
          ) : null}
          {isMfaStep ? (
            <>
              <p className="shadcn-login-copy">
                Enter the verification code sent to {currentUserEmail}.
              </p>
              <label className="shadcn-login-field" htmlFor="mfa-code">
                <span>Verification code</span>
                <input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                />
              </label>
              <div className="shadcn-login-actions">
                <Button
                  className="shadcn-login-secondary"
                  type="button"
                  color="transparent"
                  onClick={resetMfaStep}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  className="shadcn-login-secondary"
                  type="button"
                  color="transparent"
                  onClick={resendMfaLogin}
                  disabled={loading}
                >
                  Resend code
                </Button>
              </div>
            </>
          ) : (
            <>
              <label className="shadcn-login-field" htmlFor="email">
                <span>User</span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  aria-invalid={
                    formik.touched.email && Boolean(formik.errors.email)
                  }
                />
              </label>
              <label className="shadcn-login-field" htmlFor="password">
                <span>Password</span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="off"
                  value={formik.values.password}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  aria-invalid={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                />
              </label>
              <Link className="shadcn-login-forgot" href="/auth/forgot-password">
                Forgot password?
              </Link>
            </>
          )}
          <Button
            className="shadcn-login-submit"
            type={isMfaStep ? "button" : "submit"}
            color="primary"
            block
            disabled={loading}
            onClick={isMfaStep ? verifyMfaLogin : undefined}
          >
            {loading && (
              <CircularProgress
                size={18}
                color="inherit"
                style={{ marginRight: "10px" }}
              />
            )}
            {isMfaStep ? "Verify code" : "Sign in"}
          </Button>
        </form>
        <div className="shadcn-login-copyright">
          © 2026 PartSites LLC. The action behind the transaction.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={6} md={4}>
          <form onSubmit={handleFormSubmit}>
            <Card login className={classes[cardAnimaton]}>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="rose"
              >
                <h4 className={classes.cardTitle}>Login</h4>
              </CardHeader>
              <CardBody>
                {alert.show ? (
                  <MuiAlert
                    onClose={() => setAlert({ ...alert, show: false })}
                    severity={alert.severity}
                  >
                    {alert.message}
                  </MuiAlert>
                ) : null}
                {isMfaStep ? (
                  <>
                    <p style={{ marginTop: 0, marginBottom: 16 }}>
                      Enter the verification code sent to {currentUserEmail}.
                    </p>
                    <CustomInput
                      labelText="Verification code"
                      id="mfa-code"
                      name="mfa-code"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        onChange: (event) => setMfaCode(event.target.value),
                        value: mfaCode,
                        inputMode: "numeric",
                        maxLength: 6,
                      }}
                      value={mfaCode}
                    />
                  </>
                ) : (
                  <>
                    <CustomInput
                      labelText="User"
                      id="email"
                      name="email"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        onChange: formik.handleChange,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Email className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                      value={formik.values.email}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                    />
                    <CustomInput
                      labelText="Password"
                      id="password"
                      name="password"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        onChange: formik.handleChange,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                            </Icon>
                          </InputAdornment>
                        ),
                        type: "password",
                        autoComplete: "off",
                      }}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.password && Boolean(formik.errors.password)
                      }
                    />
                    <Link href="/auth/forgot-password" onClick={() => {}}>
                      Forgot password?
                    </Link>
                  </>
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
                {isMfaStep ? (
                  <>
                    <Button
                      type="button"
                      color="transparent"
                      simple
                      onClick={resetMfaStep}
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      color="transparent"
                      simple
                      onClick={resendMfaLogin}
                      disabled={loading}
                    >
                      Resend code
                    </Button>
                  </>
                ) : null}
                <Button
                  type={isMfaStep ? "button" : "submit"}
                  color="rose"
                  simple
                  size="lg"
                  block
                  disabled={loading}
                  onClick={isMfaStep ? verifyMfaLogin : undefined}
                >
                  {loading && (
                    <CircularProgress
                      size={18}
                      color="inherit"
                      style={{ marginRight: "10px" }}
                    />
                  )}
                  {isMfaStep ? "Verify code" : "Login"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </GridItem>
      </GridContainer>
    </div>
  );
}
